// WB-M1A (doc 23 §13.4.4, §13.4.7-§13.4.9) -- the Borna-owned whiteboard of
// one AI Tutor conversation: load, version, autosave, stale state.
//
// Deliberately its own module and its own API client. It knows nothing of
// the `aiTutor` chat module, the tutor upstream or the event stream, so the
// board loads and saves whatever the chat is doing (WB-M1-D13).
//
// What is NOT in Vuex state, on purpose: the scene itself (a large object
// Vue must not walk) and the autosave engine's timers and in-flight request.
// The view receives the decoded scene from `load` / `reload` and hands the
// editor's current scene over lazily (`getScene`) at save time, so a save
// always carries the newest scene.
//
// Nothing is kept in localStorage: Subway's row is the only authority.

import * as boardApi from '@/services/aiTutor/boardApi'
import * as boardScene from '@/services/aiTutor/boardScene'
import { isOnline } from '@/services/connectivity'

// Doubled in tests the same way the `aiTutor` module's collaborators are.
export const collaborators = { api: boardApi, codec: boardScene }

// Doc 23 §13.4.9.
export const BOARD_AUTOSAVE_DEBOUNCE_MS = 1500
export const BOARD_AUTOSAVE_MAX_WAIT_MS = 10000
export const BOARD_SAVE_RETRY_DELAYS_MS = [2000, 5000, 15000]

const CONFLICT_CODE = 'AI_TUTOR_BOARD_VERSION_CONFLICT'

function freshEngine(seq) {
  return {
    // Bumped on every load / reset so a response for an older board is
    // dropped instead of touching the current one.
    seq,
    debounceTimer: null,
    maxWaitTimer: null,
    retryTimer: null,
    inFlight: null,
    // Whether the editor holds a scene Subway does not have.
    dirty: false,
    // {sceneVersion, getScene} -- the newest scene the editor reported.
    latest: null,
    savedSceneVersion: null,
    retryIndex: 0,
  }
}

let engine = freshEngine(0)

function clearTimers() {
  clearTimeout(engine.debounceTimer)
  clearTimeout(engine.maxWaitTimer)
  clearTimeout(engine.retryTimer)
  engine.debounceTimer = null
  engine.maxWaitTimer = null
  engine.retryTimer = null
}

function resetEngine() {
  clearTimers()
  engine = freshEngine(engine.seq + 1)
}

function errorCode(err) {
  return err && err.body && typeof err.body === 'object' ? err.body.code : null
}

/** @returns {'conflict'|'transient'|'rejected'} */
export function classifySaveError(err) {
  const status = err && typeof err.status === 'number' ? err.status : 0
  if (status === 409 && errorCode(err) === CONFLICT_CODE) return 'conflict'
  if (status === 0 || status >= 500 || status === 429 || status === 408) {
    return 'transient'
  }
  return 'rejected'
}

function toSaveError(err, kind) {
  return {
    kind,
    status: err && typeof err.status === 'number' ? err.status : 0,
    code: errorCode(err) || null,
  }
}

// GET failures: which bounded state the Workspace shows.
function loadFailureStatus(err) {
  const status = err && typeof err.status === 'number' ? err.status : 0
  const code = errorCode(err)
  if (code === 'BOARD_SCENE_UNSUPPORTED') return 'error'
  // The server-driven capability is off: the routes answer a bare 404.
  if (status === 404 && !code) return 'unavailable'
  if (status === 404) return 'not_found'
  if (status === 403 || status === 401) return 'forbidden'
  if (status === 409) return 'not_active'
  return 'error'
}

function schedule(ctx) {
  clearTimeout(engine.debounceTimer)
  engine.debounceTimer = setTimeout(() => fire(ctx), BOARD_AUTOSAVE_DEBOUNCE_MS)
  if (!engine.maxWaitTimer) {
    engine.maxWaitTimer = setTimeout(
      () => fire(ctx),
      BOARD_AUTOSAVE_MAX_WAIT_MS
    )
  }
}

// Sends the newest scene unless a save is already in flight -- exactly one
// request at a time; the in-flight one re-fires on completion if the editor
// moved on meanwhile.
function fire(ctx) {
  clearTimeout(engine.debounceTimer)
  clearTimeout(engine.maxWaitTimer)
  engine.debounceTimer = null
  engine.maxWaitTimer = null
  if (engine.inFlight) return engine.inFlight
  if (!engine.dirty || !engine.latest) return Promise.resolve()
  return save(ctx)
}

function save(ctx) {
  const { commit, state } = ctx
  const seq = engine.seq
  const latest = engine.latest
  clearTimeout(engine.retryTimer)
  engine.retryTimer = null

  let scene
  try {
    scene = collaborators.codec.encodeBoardScene(latest.getScene())
  } catch (err) {
    commit('setSaveState', {
      saveState: 'failed',
      saveError: { kind: 'rejected', status: 0, code: 'BOARD_SCENE_ENCODE' },
    })
    return Promise.resolve()
  }

  engine.dirty = false
  const sentSceneVersion = latest.sceneVersion
  commit('setSaveState', {
    saveState: engine.retryIndex > 0 ? 'retrying' : 'saving',
    saveError: null,
  })

  const request = collaborators.api
    .saveBoard(state.conversationId, { expectedVersion: state.version, scene })
    .then(
      result => {
        if (seq !== engine.seq) return undefined
        engine.inFlight = null
        engine.savedSceneVersion = sentSceneVersion
        engine.retryIndex = 0
        commit('setSaved', {
          version: result.version,
          savedAt: result.updatedAt || null,
        })
        // Edits made while this request was in flight: one more save, with
        // the newest scene (coalesced, never queued one by one).
        if (engine.dirty) {
          commit('setSaveState', { saveState: 'dirty', saveError: null })
          return fire(ctx)
        }
        return undefined
      },
      err => {
        if (seq !== engine.seq) return
        engine.inFlight = null
        engine.dirty = true
        const kind = classifySaveError(err)
        if (kind === 'conflict') {
          // Another save landed first. Autosave stops; nothing is merged and
          // nothing is overwritten (doc 23 §13.4.8).
          clearTimers()
          commit('setStale', {
            currentVersion:
              err.body && typeof err.body.currentVersion === 'number'
                ? err.body.currentVersion
                : null,
          })
          return
        }
        if (
          kind === 'transient' &&
          engine.retryIndex < BOARD_SAVE_RETRY_DELAYS_MS.length
        ) {
          const delay = BOARD_SAVE_RETRY_DELAYS_MS[engine.retryIndex]
          engine.retryIndex += 1
          commit('setSaveState', {
            saveState: 'retrying',
            saveError: toSaveError(err, kind),
          })
          engine.retryTimer = setTimeout(() => {
            engine.retryTimer = null
            fire(ctx)
          }, delay)
          return
        }
        clearTimers()
        commit('setSaveState', {
          saveState: 'failed',
          saveError: toSaveError(err, kind),
        })
      }
    )
  engine.inFlight = request
  return request
}

function initialState() {
  return {
    conversationId: null,
    boardId: null,
    // The board version Subway last confirmed; sent as `expectedVersion`.
    version: null,
    limits: null,
    // 'idle' | 'loading' | 'ready' | 'unavailable' | 'not_found' |
    // 'forbidden' | 'not_active' | 'error'
    status: 'idle',
    // 'clean' | 'saved' | 'dirty' | 'saving' | 'retrying' | 'failed' | 'stale'
    // P40 QA-24: 'clean' = loaded and not changed since, nothing to save;
    // 'saved' only after Subway confirmed a save (never on open).
    saveState: 'clean',
    // {kind: 'transient'|'rejected', status, code} for 'retrying' / 'failed'.
    saveError: null,
    // Subway's version when a 409 said this tab is stale.
    staleVersion: null,
    lastSavedAt: null,
  }
}

export default {
  namespaced: true,
  state: initialState(),
  getters: {
    isReady: state => state.status === 'ready',
    isStale: state => state.saveState === 'stale',
    // Unsaved work the student should know about before leaving.
    hasUnsavedChanges: state =>
      state.status === 'ready' &&
      ['dirty', 'saving', 'retrying', 'failed'].indexOf(state.saveState) !== -1,
  },
  mutations: {
    reset(state) {
      Object.assign(state, initialState())
    },
    setLoading(state, conversationId) {
      Object.assign(state, initialState(), {
        conversationId,
        status: 'loading',
      })
    },
    setReady(state, { boardId, version, limits }) {
      state.boardId = boardId
      state.version = version
      state.limits = limits || null
      state.status = 'ready'
      state.saveState = 'clean'
      state.saveError = null
      state.staleVersion = null
    },
    setLoadFailed(state, status) {
      state.status = status
    },
    setSaveState(state, { saveState, saveError }) {
      state.saveState = saveState
      state.saveError = saveError || null
    },
    setSaved(state, { version, savedAt }) {
      state.version = version
      state.lastSavedAt = savedAt
      state.saveState = 'saved'
      state.saveError = null
    },
    setStale(state, { currentVersion }) {
      state.saveState = 'stale'
      state.saveError = null
      state.staleVersion = currentVersion
    },
  },
  actions: {
    /**
     * Loads (and on first open creates) the conversation's board from
     * Subway. Never touches the chat or the tutor upstream.
     * @param {string} conversationId
     * @returns {Promise<{ok: boolean, scene?: {elements: object[], files: object,
     *   view: object}}>} the decoded scene to mount, when loaded
     */
    async load({ commit }, conversationId) {
      resetEngine()
      const seq = engine.seq
      commit('setLoading', conversationId)
      try {
        const board = await collaborators.api.getBoard(conversationId)
        if (seq !== engine.seq) return { ok: false }
        const scene = collaborators.codec.decodeBoardScene(board.scene)
        // WB-M1B hook: files a scene needs are produced before the editor
        // mounts. A no-op in M1A.
        const files = await collaborators.codec.rehydrateBoardFiles(scene)
        if (seq !== engine.seq) return { ok: false }
        engine.savedSceneVersion = collaborators.codec.sceneVersionOf(
          scene.elements
        )
        commit('setReady', {
          boardId: board.boardId,
          version: board.version,
          limits: board.limits,
        })
        return { ok: true, scene: { ...scene, files: files || {} } }
      } catch (err) {
        if (seq !== engine.seq) return { ok: false }
        commit(
          'setLoadFailed',
          loadFailureStatus(
            err && err.code ? { body: { code: err.code } } : err
          )
        )
        return { ok: false }
      }
    },

    /**
     * After a stale conflict (or on request): fetches the persisted board
     * again. The caller replaces the editor's scene with the result.
     */
    reload({ state, dispatch }) {
      if (!state.conversationId) return Promise.resolve({ ok: false })
      return dispatch('load', state.conversationId)
    },

    /**
     * The editor is mounted with the loaded scene: its scene version is the
     * saved baseline, whatever the editor's own restore did to it.
     * @param {number} sceneVersion
     */
    markClean({ commit, state }, sceneVersion) {
      if (state.status !== 'ready' || state.saveState === 'stale') return
      clearTimers()
      engine.savedSceneVersion = sceneVersion
      engine.latest = null
      engine.dirty = false
      engine.retryIndex = 0
      commit('setSaveState', { saveState: 'clean', saveError: null })
    },

    /**
     * The editor reported its scene. A new scene version makes the board
     * dirty and (re)starts the debounce; the same version is not a change.
     * `getScene` is called at save time, so the newest scene is what goes
     * out however many changes were reported.
     * @param {{sceneVersion: number, getScene: function(): object}} change
     */
    noteChange(ctx, { sceneVersion, getScene }) {
      const { commit, state } = ctx
      if (state.status !== 'ready' || state.saveState === 'stale') return
      const known = engine.latest
        ? engine.latest.sceneVersion
        : engine.savedSceneVersion
      if (sceneVersion === known) {
        if (engine.latest) engine.latest.getScene = getScene
        return
      }
      engine.latest = { sceneVersion, getScene }
      engine.dirty = true
      // After the retries were exhausted nothing is sent again until the
      // student asks: the NOT SAVED state stays on screen.
      if (state.saveState === 'failed') return
      if (!engine.inFlight && !engine.retryTimer) {
        commit('setSaveState', { saveState: 'dirty', saveError: null })
      }
      schedule(ctx)
    },

    /**
     * Saves now and waits: used before in-app navigation. One attempt, no
     * retry ladder. Resolves to whether the board is saved.
     * @returns {Promise<boolean>}
     */
    async flush(ctx) {
      const { state } = ctx
      if (state.status !== 'ready') return true
      clearTimers()
      if (engine.inFlight) {
        try {
          await engine.inFlight
        } catch (err) {
          // classified by the request's own handler
        }
      }
      if (state.saveState === 'stale') return false
      if (!engine.dirty) {
        return state.saveState === 'saved' || state.saveState === 'clean'
      }
      // P40 QA-28: offline a save can only fail (or hang): none is sent, and
      // the caller asks the Student instead of waiting.
      if (!isOnline()) return false
      clearTimers()
      engine.retryIndex = BOARD_SAVE_RETRY_DELAYS_MS.length
      await save(ctx)
      clearTimers()
      return state.saveState === 'saved'
    },

    /** The student's manual retry from the NOT SAVED state. */
    retrySave(ctx) {
      const { state } = ctx
      if (state.status !== 'ready' || state.saveState === 'stale') {
        return Promise.resolve()
      }
      if (!engine.latest) return Promise.resolve()
      clearTimers()
      engine.retryIndex = 0
      engine.dirty = true
      return fire(ctx)
    },

    /**
     * Page hide / unload: a best-effort keepalive save of unsaved work.
     * Never a persistence guarantee, and it changes no state here.
     * @returns {boolean} whether a request was handed to the browser
     */
    flushOnPageHide({ state }) {
      if (
        state.status !== 'ready' ||
        state.saveState === 'stale' ||
        !engine.dirty ||
        !engine.latest ||
        engine.inFlight
      ) {
        return false
      }
      try {
        const scene = collaborators.codec.encodeBoardScene(
          engine.latest.getScene()
        )
        return collaborators.api.saveBoardKeepalive(state.conversationId, {
          expectedVersion: state.version,
          scene,
        })
      } catch (err) {
        return false
      }
    },

    reset({ commit }) {
      resetEngine()
      commit('reset')
    },
  },
}
